import { observer } from 'mobx-react-lite';
import { useEffect, useState, useMemo } from 'react';
import Popup from '../../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../../hooks/useStore';
import CommonButton from '../../../../../components/common-components/button/CommonButton';
import CommonDropdown, {
  Option,
} from '../../../../../components/common-components/dropdown/CommonDropdown';
import styles from './SwitchHospital.module.css';
import { IHospitalResult, ITag } from '../../../../../models/hospital/HospitalModel';

const SwitchHospital = observer(() => {
  const { popupStore, hospitalFilesStore, hospitalStore, uiStore } = useStore();
  const [error, setError] = useState<string>('');
  const [tempSelectedHospital, setTempSelectedHospital] = useState<IHospitalResult | null>(null);
  const [tempSelectedTag, setTempSelectedTag] = useState<ITag | null>(null);
  const tagOptions: Option<number>[] = useMemo(
    () =>
      hospitalFilesStore.tags.map(tag => ({
        value: tag.id,
        label: tag.prefix,
      })),
    [hospitalFilesStore.tags]
  );
  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isSwitchHospitalOpen) return;

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
        setError('Hastaneler yüklenirken bir hata oluştu');
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isSwitchHospitalOpen]);

  useEffect(() => {
    if (popupStore.isSwitchHospitalOpen) {
      setTempSelectedHospital(hospitalFilesStore.selectedHospital);
      setTempSelectedTag(hospitalFilesStore.selectedTag);
    }
  }, [
    popupStore.isSwitchHospitalOpen,
    hospitalFilesStore.selectedHospital,
    hospitalFilesStore.selectedTag,
  ]);

  const hospitalOptions: Option<number>[] = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is IHospitalResult & { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );
  const handleTagChange = (value: number | null) => {
    if (value !== null) {
      const selectedTag = hospitalFilesStore.tags.find(tag => tag.id == value);
      if (selectedTag) {
        setTempSelectedTag(selectedTag);
      }
    } else {
      setTempSelectedTag(null);
    }
  };
  const handleChange = async (value: number | null) => {
    if (value) {
      const selectedHospital = hospitalStore.hospitalList.find(h => h.id == value);
      if (selectedHospital) {
        await hospitalFilesStore.getTagsByHospitalId(selectedHospital.id);
        setTempSelectedTag(null);
      }
      if (selectedHospital) {
        setTempSelectedHospital(selectedHospital);
        setError('');
      }
    } else {
      setTempSelectedHospital(null);
      setTempSelectedTag(null);
    }
  };
  const handleSave = () => {
    if (!tempSelectedHospital) {
      setError('Hastane seçimi zorunludur');
      return;
    }
    if (!tempSelectedTag) {
      setError('Etiket seçimi zorunludur');
      return;
    }
    hospitalFilesStore.setSelectedHospital(tempSelectedHospital);
    hospitalFilesStore.setSelectedTag(tempSelectedTag);
    popupStore.setIsSwitchHospitalOpen(false);
  };
  const handleClose = () => {
    setError('');
    setTempSelectedHospital(null);
    setTempSelectedTag(null);
    popupStore.setIsSwitchHospitalOpen(false);
  };

  if (uiStore.isLoading) {
    return null;
  }

  return (
    <Popup isOpen={popupStore.isSwitchHospitalOpen} onClose={handleClose} title="Hastane Seçimi">
      <div className={styles.container}>
        <CommonDropdown<number>
          options={hospitalOptions}
          value={tempSelectedHospital?.id || null}
          onChange={handleChange}
          label="Hastane Seçiniz"
          placeholder="Hastane seçin"
          error={error}
        />
        <CommonDropdown<number>
          options={tagOptions}
          value={tempSelectedTag?.id || null}
          onChange={handleTagChange}
          label="Etiket No Seçiniz"
          placeholder="Etiket No seçin"
          className={styles.tagDropdown}
          disabled={!tempSelectedHospital}
          error={!tempSelectedTag && error === 'Etiket seçimi zorunludur' ? error : undefined}
        />
        <div className={styles.buttonContainer}>
          <CommonButton type="primary" onClick={handleSave}>
            Kaydet
          </CommonButton>
        </div>
      </div>
    </Popup>
  );
});

export default SwitchHospital;
